import { Add, Bookmark, BookmarkBorder, Star } from '@mui/icons-material';
import {
  Box,
  Checkbox,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Tooltip,
} from '@mui/material';
import PropTypes from 'prop-types';
import { useContext, useEffect, useMemo, useState } from 'react';

import { Context } from '../../service/Context';
import { formatTemporadaColapsada, getInicialesNombre } from '../../utils/UtilesComun';
import {
  AvatarApp,
  ButtonApp,
  DialogApp,
  estiloBotonTablaJugador,
  IconButtonApp,
  TypographyApp,
  VisibilidadIconApp,
} from '../custom/CustomComponents';

const BotonListasConJugador = ({
  idJugador,
  enLista = false,
  badge = false,
  compact = false,
  listasConJugador: listasIniciales,
  onListasChange,
  sx = {},
}) => {
  const {
    listasAccesibles,
    obtenerListasAccesibles,
    obtenerListasConJugador,
    insertarJugadorLista,
    eliminarJugadorLista,
    idUsuario,
  } = useContext(Context);

  const [open, setOpen] = useState(false);
  const [listasJugador, setListasJugador] = useState([]);
  const [listasDisponibles, setListasDisponibles] = useState(listasAccesibles || []);
  const [loading, setLoading] = useState(false);

  // Sincronizar listas del jugador para el estado del icono/badge
  useEffect(() => {
    let isMounted = true;
    async function sincronizarListasJugador() {
      if (listasIniciales) {
        setListasJugador(listasIniciales);
      } else if (idJugador !== undefined) {
        const listasDelJugador = await obtenerListasConJugador(idJugador);
        if (isMounted) {
          setListasJugador(listasDelJugador || []);
        }
      }
    }
    sincronizarListasJugador();
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idJugador, listasIniciales]);

  // Cargar las listas accesibles únicamente cuando se abre el diálogo
  useEffect(() => {
    let isMounted = true;
    async function cargarListasAccesiblesModal() {
      if (!open) {
        return;
      }
      if (!listasAccesibles || listasAccesibles.length === 0) {
        setLoading(true);
        const accesibles = await obtenerListasAccesibles();
        if (isMounted) {
          setListasDisponibles(accesibles || []);
          setLoading(false);
        }
      } else {
        setListasDisponibles(listasAccesibles);
      }
    }
    cargarListasAccesiblesModal();
    return () => {
      isMounted = false;
    };
  }, [open, listasAccesibles, obtenerListasAccesibles]);

  const enAlgunaLista = listasJugador.length > 0;

  const listasPorVisibilidad = useMemo(() => {
    const grupos = {
      FAVORITAS: [],
      PRIVADA: [],
      COMPARTIDA: [],
      PUBLICA: [],
    };
    listasDisponibles.forEach((l) => {
      if (l.isFavorita) {
        grupos.FAVORITAS.push(l);
      } else {
        const vis = (l.visibilidad || 'PRIVADA').toUpperCase();
        if (grupos[vis]) {
          grupos[vis].push(l);
        } else {
          grupos.PRIVADA.push(l);
        }
      }
    });
    return grupos;
  }, [listasDisponibles]);

  if (idJugador === undefined) {
    return null;
  }

  const handleClick = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleToggle = async (idLista) => {
    let nuevasListas = [...listasJugador];
    if (listasJugador.includes(idLista)) {
      await eliminarJugadorLista(idLista, idJugador);
      nuevasListas = nuevasListas.filter((id) => id !== idLista);
    } else {
      await insertarJugadorLista(idLista, idJugador);
      nuevasListas.push(idLista);
    }
    setListasJugador(nuevasListas);
    if (onListasChange) {
      onListasChange(nuevasListas);
    }
  };
  const renderListaItem = (lista) => (
    <ListItemButton
      key={lista.idLista}
      onClick={() => handleToggle(lista.idLista)}
      disabled={loading}
      sx={{
        py: 0.75,
        px: 2,
        display: 'flex',
        alignItems: 'center',
        minHeight: 'auto',
      }}
    >
      <ListItemIcon sx={{ minWidth: 36 }}>
        <Checkbox
          edge="start"
          checked={listasJugador.includes(lista.idLista)}
          tabIndex={-1}
          disableRipple
          size="small"
          sx={{ p: 0 }}
        />
      </ListItemIcon>
      <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
        <TypographyApp
          sx={{
            fontWeight: 500,
            fontSize: '0.9rem',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {lista.nombre}
        </TypographyApp>
        {lista.temporada && (
          <Box
            sx={{
              px: 0.75,
              py: 0.1,
              borderRadius: '4px',
              backgroundColor: '#f1f5f9',
              border: '1px solid #e2e8f0',
              color: 'text.secondary',
              fontSize: '0.72rem',
              fontWeight: 600,
              flexShrink: 0,
            }}
          >
            {formatTemporadaColapsada(lista.temporada)}
          </Box>
        )}
      </Box>
      <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexShrink: 0, ml: 1.5 }}>
        {lista.isFavorita && (
          <Tooltip title="Favorita">
            <Star sx={{ color: 'primary.main', fontSize: '1.1rem' }} />
          </Tooltip>
        )}
        <Tooltip title={`Visibilidad: ${lista.visibilidad || 'PRIVADA'}`}>
          <VisibilidadIconApp visibilidad={lista.visibilidad} sx={{ color: 'text.secondary', fontSize: '1.15rem' }} />
        </Tooltip>
        {lista.nombreUsuario && (
          <Tooltip title={`Creado por: ${lista.nombreUsuario}`} arrow>
            <AvatarApp size={22} esPropio={lista.idUsuario === idUsuario} sx={{ fontSize: '0.62rem' }}>
              {getInicialesNombre(lista.nombreUsuario)}
            </AvatarApp>
          </Tooltip>
        )}
      </Box>
    </ListItemButton>
  );

  return (
    <>
      {badge ? (
        <Tooltip
          title={
            enAlgunaLista
              ? `En ${listasJugador.length} ${listasJugador.length === 1 ? 'lista' : 'listas'} (clic para gestionar)`
              : 'Añadir a lista'
          }
          arrow
        >
          <Box
            onClick={(e) => {
              e.stopPropagation();
              handleClick();
            }}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: compact ? 0 : '5px',
              height: 24,
              width: compact ? 26 : 'auto',
              px: compact ? 0 : 1,
              borderRadius: '5px',
              fontSize: '0.74rem',
              fontWeight: 600,
              cursor: 'pointer',
              userSelect: 'none',
              flexShrink: 0,
              transition: 'all 0.15s ease-in-out',
              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.08)',
              backgroundColor: enAlgunaLista ? '#dcfce7' : '#f1f5f9',
              color: enAlgunaLista ? '#15803d' : '#1e293b',
              border: enAlgunaLista ? '1px solid #86efac' : '1px solid #cbd5e1',
              '&:hover': {
                boxShadow: '0 2px 5px rgba(0, 0, 0, 0.12)',
                filter: 'brightness(0.97)',
                transform: 'translateY(-0.5px)',
                backgroundColor: enAlgunaLista ? '#bbf7d0' : '#e2e8f0',
                borderColor: enAlgunaLista ? '#4ade80' : '#94a3b8',
                color: enAlgunaLista ? '#14532d' : '#0f172a',
              },
              ...sx,
            }}
          >
            {enAlgunaLista ? (
              <Bookmark sx={{ fontSize: '0.86rem', color: '#16a34a' }} />
            ) : (
              <BookmarkBorder sx={{ fontSize: '0.86rem', color: '#475569' }} />
            )}
            {!compact && <span>{enAlgunaLista ? 'En lista' : 'Lista'}</span>}
          </Box>
        </Tooltip>
      ) : enLista ? (
        <IconButtonApp onClick={handleClick} enLista={enLista} style={estiloBotonTablaJugador(enAlgunaLista)} border>
          <Add />
        </IconButtonApp>
      ) : (
        <ButtonApp sx={{ px: 2, ...estiloBotonTablaJugador(enAlgunaLista) }} onClick={handleClick}>
          <Add sx={{ mr: 0.5 }} />
          <TypographyApp sx={{ fontSize: '1rem' }}>Añadir a lista</TypographyApp>
        </ButtonApp>
      )}
      <DialogApp open={open} onClose={handleClose} title="Mis Listas" maxWidth="sm">
        <List
          dense
          sx={{
            maxHeight: '90vh',
            overflow: 'auto',
            pt: 0,
            '& .MuiListSubheader-root': {
              position: 'sticky',
              top: 0,
              zIndex: 10,
              bgcolor: 'white', // Color sólido para evitar transparencias al scrollear
              fontWeight: 'bold',
              lineHeight: '36px',
              borderBottom: '0.5px solid rgba(0,0,0,0.05)',
            },
          }}
        >
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : listasDisponibles && listasDisponibles.length > 0 ? (
            <>
              {listasPorVisibilidad.FAVORITAS.length > 0 && (
                <>
                  <ListSubheader sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main' }}>
                    <Star sx={{ fontSize: '1.2rem' }} /> MIS FAVORITAS
                  </ListSubheader>
                  {listasPorVisibilidad.FAVORITAS.map(renderListaItem)}
                  <Divider sx={{ my: 1 }} />
                </>
              )}
              {listasPorVisibilidad.PRIVADA.length > 0 && (
                <>
                  <ListSubheader>LISTAS PRIVADAS</ListSubheader>
                  {listasPorVisibilidad.PRIVADA.map(renderListaItem)}
                  <Divider sx={{ my: 1 }} />
                </>
              )}
              {listasPorVisibilidad.COMPARTIDA.length > 0 && (
                <>
                  <ListSubheader>LISTAS COMPARTIDAS</ListSubheader>
                  {listasPorVisibilidad.COMPARTIDA.map(renderListaItem)}
                  <Divider sx={{ my: 1 }} />
                </>
              )}
              {listasPorVisibilidad.PUBLICA.length > 0 && (
                <>
                  <ListSubheader>LISTAS PÚBLICAS</ListSubheader>
                  {listasPorVisibilidad.PUBLICA.map(renderListaItem)}
                </>
              )}
            </>
          ) : (
            <ListItem>
              <ListItemText primary="No hay listas disponibles" />
            </ListItem>
          )}
        </List>
      </DialogApp>
    </>
  );
};

BotonListasConJugador.propTypes = {
  idJugador: PropTypes.number,
  enLista: PropTypes.bool,
  badge: PropTypes.bool,
  compact: PropTypes.bool,
  listasConJugador: PropTypes.array,
  onListasChange: PropTypes.func,
  style: PropTypes.object,
};

export default BotonListasConJugador;
